import RetryHandler from './RetryHandler.js';
import { rollbarService } from '../utils/rollbarConfig';

export default class HttpClient {
  constructor(apiHost = null, clientInstance = null, retryOptions = {}) {
    this.apiHost = apiHost || this.getApiHost();
    this.apiVersion = 'v2';
    this.clientInstance = clientInstance;
    this.retryHandler = new RetryHandler(retryOptions);
  }

  getApiHost() {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }

    return 'http://localhost:3000';
  }

  getVersionedPath(path) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Health check endpoints remain unversioned
    if (
      normalizedPath.startsWith('/health') ||
      normalizedPath.startsWith('/ready')
    ) {
      return normalizedPath;
    }

    const apiVersionedPathMatch = normalizedPath.match(/^\/api\/v\d+(\/.*|$)/);
    if (apiVersionedPathMatch) {
      return `/api/${this.apiVersion}${apiVersionedPathMatch[1] || ''}`;
    }

    const versionedPathMatch = normalizedPath.match(/^\/v\d+(\/.*|$)/);
    if (versionedPathMatch) {
      return `/api/${this.apiVersion}${versionedPathMatch[1] || ''}`;
    }

    if (normalizedPath.startsWith('/api/')) {
      return normalizedPath;
    }

    // Add versioning to all other paths
    return `/api/${this.apiVersion}${normalizedPath}`;
  }

  // Response adapter methods for new standardized API format
  isStandardizedResponse(responseData) {
    return (
      responseData &&
      typeof responseData === 'object' &&
      'success' in responseData &&
      'meta' in responseData
    );
  }

  extractResponseData(responseData) {
    if (this.isStandardizedResponse(responseData)) {
      // New standardized format: { success: true, data: {...}, meta: {...} }
      return {
        data: responseData.data,
        meta: responseData.meta,
        success: responseData.success,
        message: responseData.message,
        pagination: responseData.meta?.pagination,
      };
    }

    // Legacy format: { data: [...], meta: {...} } or direct data
    if (
      responseData &&
      typeof responseData === 'object' &&
      'data' in responseData
    ) {
      return {
        data: responseData.data,
        meta: responseData.meta || {},
        success: true,
        pagination: responseData.meta,
      };
    }

    // Direct data response (legacy)
    return {
      data: responseData,
      meta: {},
      success: true,
    };
  }

  extractErrorData(errorData) {
    if (this.isStandardizedResponse(errorData)) {
      // New standardized error format: { success: false, errors: [...], meta: {...} }
      return {
        errors: errorData.errors || [],
        meta: errorData.meta || {},
        success: false,
      };
    }

    // Legacy error formats
    if (errorData.errors && Array.isArray(errorData.errors)) {
      return {
        errors: errorData.errors,
        meta: {},
        success: false,
      };
    }

    // Single error message
    return {
      errors: [
        { message: errorData.error || errorData.message || 'Unknown error' },
      ],
      meta: {},
      success: false,
    };
  }

  async makeRequestWithRetry(requestFunction, requestOptions = {}) {
    return await this.retryHandler.executeWithRetry(async () => {
      const response = await requestFunction();

      if (!response.ok) {
        // Process the error response to get structured error details
        let structuredError;
        try {
          const errorData = await response.json();
          const extractedError = this.extractErrorData(errorData);

          structuredError = new Error(
            `Request failed: ${response.status} ${response.statusText}`
          );
          structuredError.errors = extractedError.errors;
          structuredError.meta = extractedError.meta;
          structuredError.status = response.status;
        } catch {
          // If we can't parse the error response as JSON, create a basic error
          structuredError = new Error(
            `Request failed: ${response.status} ${response.statusText}`
          );
          structuredError.status = response.status;
          structuredError.errors = [{ message: `HTTP ${response.status}` }];
          structuredError.meta = {};
        }

        // Log to Rollbar for 5xx errors and 404 errors
        if (structuredError.status >= 500 || structuredError.status === 404) {
          rollbarService.apiError(structuredError, {
            url: requestOptions.url,
            method: requestOptions.method,
            status: structuredError.status,
            errors: structuredError.errors,
          });
        }

        throw structuredError;
      }

      return response;
    }, requestOptions.retryOptions);
  }

  // Helper method to process response for all HTTP methods
  async processResponse(response) {
    const responseData = await response.json();
    const extracted = this.extractResponseData(responseData);

    // For backward compatibility, return just the data, but attach meta as property
    if (extracted.data !== undefined) {
      const result = extracted.data;
      if (result && typeof result === 'object') {
        result._meta = extracted.meta;
        result._pagination = extracted.pagination;
      }
      return result;
    }

    return responseData;
  }

  // Helper method specifically for DELETE responses which may be empty or 204
  async processDeleteResponse(response) {
    if (response.status === 204) {
      return true;
    }

    try {
      const responseData = await response.json();
      const extracted = this.extractResponseData(responseData);

      // Handle standardized response or return raw data
      if (extracted.message || extracted.data !== undefined) {
        return extracted.data || extracted.message || true;
      }

      return responseData;
    } catch {
      return true;
    }
  }

  async get(path, token = null, queryParams = {}) {
    const versionedPath = this.getVersionedPath(path);
    let url = `${this.apiHost}${versionedPath}`;

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      url = `${url}?${queryString}`;
    }

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = { method: 'GET', headers, url };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, { method: 'GET', headers }),
      requestOptions
    );

    return await this.processResponse(response);
  }

  async post(path, token = null, body = {}) {
    const versionedPath = this.getVersionedPath(path);
    const url = `${this.apiHost}${versionedPath}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      url,
    };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, requestOptions),
      requestOptions
    );

    return await this.processResponse(response);
  }

  async getWithFullResponse(path, token = null, queryParams = {}) {
    const versionedPath = this.getVersionedPath(path);
    let url = `${this.apiHost}${versionedPath}`;

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      url = `${url}?${queryString}`;
    }

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = { method: 'GET', headers, url };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, { method: 'GET', headers }),
      requestOptions
    );

    const responseData = await response.json();
    const extracted = this.extractResponseData(responseData);

    return {
      data: extracted.data,
      meta: extracted.meta,
      message: extracted.message,
      pagination: extracted.pagination,
      headers: response.headers,
      status: response.status,
    };
  }

  async getBlobWithFullResponse(path, token = null, queryParams = {}) {
    const versionedPath = this.getVersionedPath(path);
    let url = `${this.apiHost}${versionedPath}`;

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      url = `${url}?${queryString}`;
    }

    const headers = {
      Accept: '*/*',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = { method: 'GET', headers, url };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, { method: 'GET', headers }),
      requestOptions
    );

    return {
      blob: await response.blob(),
      headers: response.headers,
      status: response.status,
      contentType: response.headers.get('Content-Type'),
    };
  }

  async postWithFullResponse(path, token = null, body = {}) {
    const versionedPath = this.getVersionedPath(path);
    const url = `${this.apiHost}${versionedPath}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      url,
    };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, requestOptions),
      requestOptions
    );

    const responseData = await response.json();
    const extracted = this.extractResponseData(responseData);

    return {
      data: extracted.data,
      meta: extracted.meta,
      message: extracted.message,
      pagination: extracted.pagination,
      headers: response.headers,
      status: response.status,
    };
  }

  async put(path, token = null, body = {}) {
    const versionedPath = this.getVersionedPath(path);
    const url = `${this.apiHost}${versionedPath}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
      url,
    };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, requestOptions),
      requestOptions
    );

    return await this.processResponse(response);
  }

  async patch(path, token = null, body = {}) {
    const versionedPath = this.getVersionedPath(path);
    const url = `${this.apiHost}${versionedPath}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
      url,
    };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, requestOptions),
      requestOptions
    );

    return await this.processResponse(response);
  }

  async delete(path, token = null) {
    const versionedPath = this.getVersionedPath(path);
    const url = `${this.apiHost}${versionedPath}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method: 'DELETE',
      headers,
      url,
    };

    const response = await this.makeRequestWithRetry(
      () => fetch(url, { method: 'DELETE', headers }),
      requestOptions
    );

    return await this.processDeleteResponse(response);
  }
}
